import React, { useState, useEffect, useRef, useContext } from "react";
import { useHistory, useParams } from "react-router-dom";
import { parseISO, format, isSameDay } from "date-fns";
import clsx from "clsx";

import { makeStyles } from "@material-ui/core/styles";
import { green, amber, red } from "@material-ui/core/colors";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import Typography from "@material-ui/core/Typography";
import Avatar from "@material-ui/core/Avatar";
import Divider from "@material-ui/core/Divider";
import Badge from "@material-ui/core/Badge";
import StarIcon from "@material-ui/icons/Star";
import StarBorderIcon from "@material-ui/icons/StarBorder";
import IconButton from "@material-ui/core/IconButton";
import VisibilityIcon from "@material-ui/icons/Visibility";
import ButtonWithSpinner from "../ButtonWithSpinner";
import Button from "@material-ui/core/Button";
import Tooltip from "@material-ui/core/Tooltip";
import CircularProgress from "@material-ui/core/CircularProgress";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import MarkdownWrapper from "../MarkdownWrapper";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import TicketPreviewModal from "../TicketPreviewModal";

const useStyles = makeStyles(theme => {
  const isDark = theme.palette.type === "dark";
  return {
    ticket: {
      position: "relative",
      transition: "background-color 0.2s",
      minHeight: 80,
      "&:hover": {
        backgroundColor: isDark ? "rgba(255,255,255,0.05)" : theme.palette.action.hover,
      },
    },
    favoriteTicket: {
      backgroundColor: isDark ? "rgba(255,215,0,0.15)" : amber[50],
    },
    pendingTicket: {
      cursor: "unset",
    },
    ticketQueueColor: {
      flex: "none",
      width: 8,
      height: "100%",
      position: "absolute",
      top: 0,
      left: 0,
    },
    ticketInfoContainer: {
      display: "flex",
      flexDirection: "column",
      flex: 1,
      marginLeft: theme.spacing(1),
      minWidth: 0,
    },
    ticketHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: theme.spacing(0.5),
    },
    contactName: {
      fontWeight: 500,
      flexShrink: 1,
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
    },
    headerRight: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
    },
    lastMessageTime: {
      flexShrink: 0,
      fontSize: "0.75rem",
      color: theme.palette.text.secondary,
    },
    favoriteButton: {
      padding: 4,
    },
    ticketBody: {
      display: "flex",
      alignItems: "center",
      width: "100%",
      minHeight: 32,
    },
    contactLastMessage: {
      flex: 1,
      minWidth: 0,
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      marginRight: theme.spacing(1),
    },
    newMessagesCount: {
      flexShrink: 0,
    },
    badgeStyle: {
      color: "white",
      backgroundColor: green[500],
    },
    actionButtonsContainer: {
      display: "flex",
      alignItems: "center",
      flexShrink: 0,
      gap: theme.spacing(0.5),
    },
    actionsOnly: {
      flex: 1,
      width: "100%",
      justifyContent: "space-between",
      gap: theme.spacing(1),
    },
    userTag: {
      backgroundColor: "#2576D2",
      color: "#ffffff",
      borderRadius: 10,
      padding: "2px 6px",
      fontSize: "0.75rem",
      flexShrink: 0,
    },
    rejectedBadgeContent: {
      backgroundColor: red[500],
      color: "white",
    },
    badgePosition: {
      position: "absolute",
      bottom: theme.spacing(2),
      left: theme.spacing(5),
    },
  };
});

const TicketListItem = ({
  ticket,
  onTicketSelect,
  onOpenIgnoreModal,
  hideFavorite = false,
  hideMessagePreview = false,
}) => {
  const classes = useStyles();
  const history = useHistory();
  const { ticketId } = useParams();
  const isMounted = useRef(true);
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const { dispatch } = useContext(TicketsContext);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewTicketData, setPreviewTicketData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [isFavorite, setIsFavorite] = useState(ticket.isFavorite);

  useEffect(() => {
    setIsFavorite(ticket.isFavorite);
  }, [ticket.isFavorite]);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleAcceptTicket = async id => {
    setLoading(true);
    try {
      const { data: updatedTicket } = await api.put(`/tickets/${id}`, {
        status: "open",
        userId: user.id,
        sendGreetingMessage: true,
      });
      
      if (updatedTicket.messageError) {
        toast.warn(updatedTicket.messageError, { autoClose: 10000 });
      }

      dispatch({
        type: "UPDATE_TICKET",
        payload: updatedTicket,
        meta: { listStatus: "open", user: user, selectedTicketId: id }, 
      });
    } catch (err) {
      toastError(err);
    } finally {
      if (isMounted.current) setLoading(false)
    }
    history.push(`/tickets/${id}`);
  };

  const handleSelectTicket = id => {
    onTicketSelect(id);
    history.push(`/tickets/${id}`);
  };

  const handleToggleFavorite = async () => {
    const newValue = !isFavorite;

    setIsFavorite(newValue);

    try {
      await api.patch(`/tickets/${ticket.id}/favorite`, {
        isFavorite: newValue
      });
    } catch (err) {
      setIsFavorite(!newValue);
      toastError(err);
    }
  };


  const handlePreview = async () => {
    if (!ticket.id || previewLoading) return;

    setPreviewLoading(true);

    try {
      const { data } = await api.get(`/tickets/${ticket.id}`, {
        params: { isPreview: true }
      });
      setPreviewTicketData(data);
      setPreviewModalOpen(true); 
    } catch (err) {
      toastError(err);
    } finally {
      if (isMounted.current) {
        setPreviewLoading(false);
      }
    }
  };

  return (
    <div key={ticket.id}>
      <ListItem
        dense
        button
        onClick={e => {
          if (ticket.status === "pending") return;
          handleSelectTicket(ticket.id);
        }}
        selected={ticketId && +ticketId === ticket.id}
        className={clsx(classes.ticket, {
          [classes.pendingTicket]: ticket.status === "pending",
          [classes.favoriteTicket]: isFavorite,
        })}
      >
        <Tooltip
          arrow
          placement="right"
          title={ticket.queue?.name || "Sin departamento"}
        >
          <span
            style={{ backgroundColor: ticket.queue?.color || "#7C7C7C" }}
            className={classes.ticketQueueColor}
          />
        </Tooltip>

        <ListItemAvatar>
          <Avatar src={ticket?.contact?.profilePicUrl} />
        </ListItemAvatar>

        <ListItemText
          disableTypography
          className={classes.ticketInfoContainer}
          primary={
            <div className={classes.ticketHeader}>
              <Typography
                noWrap
                component="span"
                variant="body2"
                color="textPrimary"
                className={classes.contactName}
              >
               {ticket.title ? ticket.title + ' | ' : ""} {ticket.contact.name}
              </Typography>

              <div className={classes.headerRight}>
                {ticket.whatsappId && (
                  <div
                    className={classes.userTag}
                    title={i18n.t("ticketsList.connectionTitle")}
                  >
                    {ticket.whatsapp?.name}
                  </div>
                )}

                {ticket.lastMessage && (
                  <Typography
                    component="span"
                    variant="body2"
                    className={classes.lastMessageTime}
                  >
                    {isSameDay(parseISO(ticket.updatedAt), new Date())
                      ? format(parseISO(ticket.updatedAt), "HH:mm")
                      : format(parseISO(ticket.updatedAt), "dd/MM/yyyy")}
                  </Typography>
                )}
              </div>
            </div>
          }
          secondary={
            <div className={classes.ticketBody}>
              {!hideMessagePreview && (
                <Typography
                  noWrap
                  component="span"
                  variant="body2"
                  color="textSecondary"
                  className={classes.contactLastMessage}
                >
                  {ticket.lastMessage ? (
                    <MarkdownWrapper>{ticket.lastMessage}</MarkdownWrapper>
                  ) : (
                    <br />
                  )}
                </Typography>
              )}

              <div
                className={clsx(classes.actionButtonsContainer, {
                  [classes.actionsOnly]: hideMessagePreview,
                })}
              >
                {!hideFavorite && (
                  <IconButton
                    className={classes.favoriteButton}
                    onClick={e => {
                      e.stopPropagation();
                      handleToggleFavorite();
                    }}
                  >
                    {isFavorite ? <StarIcon style={{ color: "gold" }} /> : <StarBorderIcon />}
                  </IconButton>
                )}

                <div className={classes.favoriteButton}>
                  {previewLoading ? (
                    <CircularProgress size={20} />
                  ) : (
                    <IconButton
                      style={{ padding: 0 }}
                      onClick={e => {
                        e.stopPropagation();
                        handlePreview();
                      }}
                      title="Preview"
                    >
                      <VisibilityIcon />
                    </IconButton>
                  )}
                </div>

                {ticket.unreadMessages > 0 && (
                  <Badge
                    badgeContent={ticket.unreadMessages}
                    classes={{ badge: classes.badgeStyle }}
                    title="Número de mensajes nuevos"
                  />
                )}

                {ticket.status === "closed" && (
                  <Badge
                    badgeContent={"cerrado"}
                    color="primary"
                    className={classes.badgePosition}
                  />
                )}

                {ticket.status === "rejected" && (
                  <Badge
                    badgeContent={"ignorado"}
                    className={classes.badgePosition}
                    classes={{ badge: classes.rejectedBadgeContent }}
                  />
                )}

                {ticket.status === "pending" && (
                  <>
                    <ButtonWithSpinner
                      color="primary"
                      variant="contained"
                      size="small"
                      loading={loading}
                      onClick={e => {
                        e.stopPropagation();
                        handleAcceptTicket(ticket.id);
                      }}
                    >
                      {i18n.t("ticketsList.buttons.accept")}
                    </ButtonWithSpinner>

                    <Button
                      size="small"
                      color="secondary"
                      variant="outlined"
                      onClick={e => {
                          e.stopPropagation();
                          if (onOpenIgnoreModal) {
                              onOpenIgnoreModal(ticket);
                          }
                      }}
                    >
                      Ignorar
                    </Button>
                  </>
                )}
              </div>
            </div>
          }
        />
      </ListItem>

      {previewTicketData && (
        <TicketPreviewModal
          open={previewModalOpen}
          onClose={() => {
            setPreviewModalOpen(false);
            setPreviewTicketData(null);
          }}
          ticket={previewTicketData}
        />
      )}

      <Divider variant="inset" component="li" />
    </div>
  );
};

const getVisibleTicketSnapshot = ticket => ({
  id: ticket.id,
  status: ticket.status,
  updatedAt: ticket.updatedAt,
  lastMessage: ticket.lastMessage,
  unreadMessages: ticket.unreadMessages,
  isFavorite: ticket.isFavorite,
  title: ticket.title,
  userId: ticket.userId,
  queueId: ticket.queueId,
  whatsappId: ticket.whatsappId,
  queueName: ticket.queue?.name,
  queueColor: ticket.queue?.color,
  whatsappName: ticket.whatsapp?.name,
  contactName: ticket.contact?.name,
  contactProfilePicUrl: ticket.contact?.profilePicUrl,
});

const areEqual = (prevProps, nextProps) => {
  const prevTicket = getVisibleTicketSnapshot(prevProps.ticket);
  const nextTicket = getVisibleTicketSnapshot(nextProps.ticket);

  return (
    prevProps.hideFavorite === nextProps.hideFavorite &&
    prevProps.hideMessagePreview === nextProps.hideMessagePreview &&
    Object.keys(prevTicket).every(key => prevTicket[key] === nextTicket[key])
  );
};

export default React.memo(TicketListItem, areEqual);
